import TnTypography from '@components/tn-typography/TnTypography';
import { createStyles, makeStyles } from '@material-ui/core';
import { DatePicker } from '@talentnow/shared-ui/form-components';
import React, { useEffect, useState } from 'react';

import { getConfigSetting } from '@components/configuration/configuration';
import { ConfigName } from '@constants/ConfigName';
import SelectDropdown, { SelectOption, SelectOptionChangeParam } from '../../../_components/forms/SelectDropdown';
import TextField from '../../../_components/forms/TextField';

interface RateAndJobValues {
  rate: number | string | null;
  rateFrequency: SelectOption | null;
  markupPercentage: number | string | null;
  earliestStartDate: string;
  estimatedHours: number | string | null;
  clients: SelectOption[] | null;
  job: SelectOption | null;
  prescreen: SelectOption | null;
  hiringCompanies: SelectOption[] | null;
}

interface RateAndJobSelectOptions {
  rateFrequency: SelectOption[];
  clients: SelectOption[];
  jobTitles: SelectOption[];
  prescreens: SelectOption[];
  hiringCompanies: SelectOption[];
}

interface RateAndJobChangeHandlers {
  rateOnChange: (value: string) => void;
  rateFrequencyOnChange: (option: SelectOptionChangeParam) => void;
  markupPercentageOnChange: (value: string) => void;
  earliestStartDateOnChange: (value: string) => void;
  estimatedHoursOnChange: (value: string) => void;
  clientsOnChange: (value: SelectOptionChangeParam) => void;
  jobOnChange: (value: SelectOptionChangeParam) => void;
  prescreenOnChange: (value: SelectOptionChangeParam) => void;
  hiringCompaniesOnChange: (value: SelectOptionChangeParam) => void;
}

interface Props {
  noWorkspaceAccess?: boolean;
  values: RateAndJobValues;
  options: RateAndJobSelectOptions;
  changeHandlers: RateAndJobChangeHandlers;
  formDisabled?: boolean;
  isEdit?: boolean;
  isConfirmRehire?: boolean;
  errors: Record<string, string>;
  touchedFields: Record<string, boolean>;
  onFieldBlur: (field: string) => void;
}

export default function RateAndJob({
  noWorkspaceAccess = false,
  values,
  options,
  changeHandlers,
  formDisabled = false,
  isEdit = false,
  isConfirmRehire = false,
  errors,
  touchedFields,
  onFieldBlur,
}: Readonly<Props>) {
  const classes = useStyles();
  const [isPrescreenEnabled, setIsPrescreenEnabled] = useState(false);

  useEffect(() => {
    getConfigSetting(ConfigName.PRESCREEN)
      .then((result) => {
        setIsPrescreenEnabled(result);
      })
      .catch((error) => {
        console.error('Error fetching PRESCREEN config:', error);
      });
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.bodyContainer}>
        {/* Section 1: Rate */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Rate
          </TnTypography>
        </div>

        <div className={classes.bodyElement}>
          <TextField
            required
            currency
            disabled={formDisabled}
            name="rate"
            label="Rate"
            value={values.rate?.toString() ?? ''}
            onChange={changeHandlers.rateOnChange}
            error={errors.rate}
            touched={touchedFields.rate}
            onBlur={() => onFieldBlur('rate')}
          />
        </div>
        <div className={classes.bodyElement}>
          <SelectDropdown
            required
            valueNotChip
            isClearable={false}
            disabled={formDisabled}
            name="rateFrequency"
            label="Frequency"
            options={options.rateFrequency}
            value={values.rateFrequency}
            onChange={changeHandlers.rateFrequencyOnChange}
            error={errors.rateFrequency}
            touched={touchedFields.rateFrequency}
            showErrorBeforeBlur={touchedFields.rateFrequency}
            onBlur={() => onFieldBlur('rateFrequency')}
          />
        </div>

        <div className={classes.bodyElement}>
          <TextField
            percentage
            disabled={formDisabled}
            type="signedFloat"
            name="markupPercentage"
            label="Markup"
            value={values.markupPercentage?.toString() ?? ''}
            onChange={changeHandlers.markupPercentageOnChange}
          />
        </div>
        <div className={classes.bodyElement}>
          <DatePicker
            required
            name="earliestStartDate"
            label="Earliest Start Date"
            disabled={formDisabled}
            value={values.earliestStartDate}
            minDate={isEdit || isConfirmRehire ? undefined : new Date().toLocaleDateString('en-US')}
            onChange={changeHandlers.earliestStartDateOnChange}
            error={errors.earliestStartDate}
            touched={!!touchedFields.earliestStartDate}
            onBlur={() => onFieldBlur('earliestStartDate')}
          />
        </div>

        <div className={classes.bodyElement}>
          <TextField
            disabled={formDisabled}
            type="float"
            name="estimatedHours"
            label="Estimated Hours"
            value={values.estimatedHours?.toString() ?? ''}
            onChange={changeHandlers.estimatedHoursOnChange}
            placeholder="e.g. 40"
          />
        </div>

        {/* Section 2: Job and Hiring Information */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Clients
          </TnTypography>
        </div>

        <div className={classes.fullWidthElement}>
          <SelectDropdown
            required
            isMulti
            name="clients"
            label="Preferred Clients"
            disabled={noWorkspaceAccess}
            options={options.clients}
            value={values.clients}
            onChange={changeHandlers.clientsOnChange}
            maxVisibleChips={2}
            error={errors.clients}
            touched={touchedFields.clients}
            showErrorBeforeBlur={touchedFields.clients}
            onBlur={() => onFieldBlur('clients')}
          />
        </div>
        <div className={classes.fullWidthElement}>
          <SelectDropdown
            required
            isMulti
            name="hiringCompanies"
            label="Hiring Companies"
            options={options.hiringCompanies}
            value={values.hiringCompanies}
            onChange={changeHandlers.hiringCompaniesOnChange}
            disabled={!values.clients || values.clients.length === 0}
            maxVisibleChips={4}
            error={errors.hiringCompanies}
            touched={touchedFields.hiringCompanies}
            showErrorBeforeBlur={touchedFields.hiringCompanies}
            onBlur={() => onFieldBlur('hiringCompanies')}
          />
        </div>

        {/* Section 3: Job and Hiring Information */}
        <div className={classes.labelContainer}>
          <TnTypography bold variant="body">
            Job
          </TnTypography>
        </div>

        <span />

        <div className={classes.bodyElement}>
          <SelectDropdown
            required
            name="job"
            label="Job Title"
            options={options.jobTitles}
            value={values.job}
            onChange={changeHandlers.jobOnChange}
            error={errors.job}
            touched={touchedFields.job}
            showErrorBeforeBlur={touchedFields.job}
            onBlur={() => onFieldBlur('job')}
          />
        </div>

        {isPrescreenEnabled && (
          <div className={classes.bodyElement}>
            <SelectDropdown
              disableSorting
              name="prescreen"
              label="Prescreen"
              options={options.prescreens}
              value={values.prescreen}
              onChange={changeHandlers.prescreenOnChange}
              disabled={!values.job || isEdit}
              error={isEdit ? 'Manage prescreens from the Prescreen tab on the candidate profile' : errors.prescreen}
              touched={touchedFields.prescreen}
              showErrorBeforeBlur={touchedFields.prescreen}
              onBlur={() => onFieldBlur('prescreen')}
            />
          </div>
        )}
      </div>
    </div>
  );
}

const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: '8px 24px 24px',
    },
    bodyContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
    },
    fullWidthElement: {
      width: '100%',
    },
    bodyElement: {
      width: 'calc(50% - 8px)',
      '&:nth-child(even)': {
        marginRight: 8,
      },
      '&:nth-child(odd)': {
        marginLeft: 8,
      },
    },
    labelContainer: {
      width: '100%',
      marginBottom: 16,
      marginTop: 16,
    },
  }),
);
